import {
  Directive,
  ElementRef,
  Input,
  Renderer2,
  OnInit,
  OnDestroy
} from '@angular/core';

@Directive({
  selector: '[appLineClamp]',
  standalone: true,
})
export class LineClampDirective implements OnInit, OnDestroy {
  /**
   * The full text to display.
   * If not bound, innerText will be used.
   */
  @Input('appLineClamp') fullText!: string;

  /** Number of lines to clamp before showing "More" */
  @Input() lines = 2;

  /** Label for the expand link */
  @Input() moreText = 'More';

  private expanded = false;
  private resizeObserver!: ResizeObserver;

  constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2) {}

  ngOnInit() {
    if (this.fullText == null) {
      this.fullText = this.el.nativeElement.innerText.trim();
    }

    const native = this.el.nativeElement;
    this.renderer.setStyle(native, 'overflow', 'hidden');
    this.renderer.setStyle(native, 'display', 'block');
    this.renderer.setStyle(native, 'width', '100%');
    this.renderer.setStyle(native, 'max-width', '100%');
    this.renderer.setStyle(native, 'min-width', '0');
    this.renderer.setStyle(native, 'word-break', 'break-word');
    this.renderer.setStyle(native, 'white-space', 'normal');

    this.resizeObserver = new ResizeObserver(() => this.applyClamp());
    this.resizeObserver.observe(native);

    setTimeout(() => this.applyClamp());
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }

  private applyClamp() {
    const el = this.el.nativeElement;
    const style = getComputedStyle(el);
    let lineHeight = parseFloat(style.lineHeight);
    if (Number.isNaN(lineHeight)) {
      const fontSize = parseFloat(style.fontSize);
      lineHeight = Number.isNaN(fontSize) ? 14 : fontSize * 1.2;
    }
    const maxHeight = this.lines * lineHeight;

    if (this.expanded) {
      this.clearClampStyles();
      el.textContent = this.fullText;
      return;
    }

    this.renderer.setStyle(el, 'display', '-webkit-box');
    this.renderer.setStyle(el, '-webkit-box-orient', 'vertical');
    this.renderer.setStyle(el, '-webkit-line-clamp', `${this.lines}`);

    el.textContent = this.fullText;

    if (el.scrollHeight <= maxHeight + 0.5) {
      return;
    }

    let low = 0;
    let high = this.fullText.length;
    let best = '';

    this.renderer.setStyle(el, '-webkit-line-clamp', 'unset');
    this.renderer.setStyle(el, 'max-height', `${maxHeight}px`);

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const snippet = this.fullText.slice(0, mid).trim();
      el.textContent = snippet + '… ' + this.moreText;

      if (el.scrollHeight <= maxHeight + 0.5) {
        best = snippet;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    el.innerHTML = '';
    this.renderer.appendChild(el, this.renderer.createText(best));
    this.renderer.appendChild(el, this.renderer.createText('… '));

    const link = this.renderer.createElement('a');
    link.textContent = this.moreText;
    this.renderer.addClass(link, 'a');
    this.renderer.listen(link, 'click', () => this.onMoreClick());
    this.renderer.appendChild(el, link);
  }

  private onMoreClick() {
    this.expanded = true;
    const el = this.el.nativeElement;
    this.clearClampStyles();
    el.textContent = this.fullText;
  }

  private clearClampStyles() {
    const el = this.el.nativeElement;
    this.renderer.setStyle(el, 'display', 'block');
    this.renderer.removeStyle(el, '-webkit-box-orient');
    this.renderer.removeStyle(el, '-webkit-line-clamp');
    this.renderer.removeStyle(el, 'max-height');
  }
}
